/**
 * API datetimes are stored/returned as UTC without a timezone suffix.
 * Mirror Flutter DateUtility.parseUtcToLocal: treat the value as UTC, then
 * display in the user's browser local timezone (based on their device/country).
 */

function normalizeApiDateString(value) {
  const s = String(value).trim();
  if (!s) return "";

  // Already has an explicit timezone — leave as-is.
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return s;

  // "YYYY-MM-DD HH:mm:ss" / "YYYY-MM-DDTHH:mm:ss" → UTC
  const withT = s.includes("T") ? s : s.replace(" ", "T");
  return withT.endsWith("Z") ? withT : `${withT}Z`;
}

/**
 * Parse an API datetime string as UTC and return a Date in local time semantics.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseApiUtcToLocal(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  try {
    const d = new Date(normalizeApiDateString(value));
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

/**
 * Format an API datetime for display in the user's local timezone.
 * @param {string|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatApiDateTime(value, options) {
  if (value == null || value === "") return "-";
  const d = parseApiUtcToLocal(value);
  if (!d) return String(value);
  try {
    return options ? d.toLocaleString(undefined, options) : d.toLocaleString();
  } catch (_) {
    return String(value);
  }
}

export default {
  parseApiUtcToLocal,
  formatApiDateTime,
};
